import { RevaluationType } from 'further-types/lp/revaluation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { groupBy, sortBy } from 'lodash';

export type ProcessedRevaluation = {
  id?: string;
  holdingClassName: string;
  holdingClassId: string;
  priorValuation: number | undefined;
  newValuation: number;
  priorUnitPrice: number | undefined;
  newUnitPrice: number;
  currentNumberOfUnits: number;
  valuationChange: number | undefined;
  valuationChangePercentage: number | undefined;
  date: Date;
  hasChange: boolean;
  isNew: boolean;
  currency: CurrencyCode;
  type?: RevaluationType;
};

function processRevaluationHistory(
  revaluations: Array<{
    holdingClassId?: string;
    currentNumberOfUnits?: number;
    unitPriceTcy: number;
    ledgerDate: Date;
    type: RevaluationType;
    revaluationId?: string;
    holdingClassName: string;
    isNew?: boolean;
    currency: CurrencyCode;
  }>,
) {
  const groupedByHoldingClass = groupBy(
    revaluations,
    (rev) => rev.holdingClassId,
  );

  const processedData: Array<ProcessedRevaluation> = [];

  Object.entries(groupedByHoldingClass).forEach(([_, classRevaluations]) => {
    const sortedRevaluations = sortBy(classRevaluations, 'ledgerDate');

    sortedRevaluations.forEach((revaluation, index) => {
      const newUnitPrice = revaluation.unitPriceTcy ?? 0;
      const newValuation =
        newUnitPrice * (revaluation.currentNumberOfUnits ?? 0);

      const priorRevaluation = index > 0 ? sortedRevaluations[index - 1] : null;

      if (!priorRevaluation) {
        // First revaluation in the series - show only new values
        processedData.push({
          id: revaluation.revaluationId,
          isNew: !revaluation.revaluationId,
          holdingClassName: revaluation.holdingClassName ?? '',
          holdingClassId: revaluation.holdingClassId ?? '',
          priorValuation: undefined,
          newValuation,
          priorUnitPrice: undefined,
          newUnitPrice,
          currentNumberOfUnits: revaluation.currentNumberOfUnits ?? 0,
          type: revaluation.type,
          valuationChange: undefined,
          valuationChangePercentage: undefined,
          date: revaluation.ledgerDate ?? new Date(),
          hasChange: false,
          currency: revaluation.currency,
        });
      } else {
        const priorUnitPrice = priorRevaluation.unitPriceTcy ?? 0;
        // Calculate prior valuation using the same number of units as current revaluation
        const priorValuation =
          priorUnitPrice * (revaluation.currentNumberOfUnits ?? 0);

        const valuationChange = newValuation - priorValuation;

        const valuationChangePercentage =
          priorValuation !== 0 ? valuationChange / priorValuation : 0;

        processedData.push({
          id: revaluation.revaluationId || `new-${revaluation.holdingClassId}`,
          isNew: !revaluation.revaluationId,
          type: revaluation.type,
          currentNumberOfUnits: revaluation.currentNumberOfUnits ?? 0,
          holdingClassName: revaluation.holdingClassName || '',
          holdingClassId: revaluation.holdingClassId || '',
          hasChange: newUnitPrice !== priorUnitPrice,
          priorValuation,
          newValuation,
          priorUnitPrice,
          newUnitPrice,
          valuationChange,
          valuationChangePercentage,
          date: revaluation.ledgerDate ?? new Date(),
          currency: revaluation.currency,
        });
      }
    });
  });

  return sortBy(processedData, (row) => row.date);
}

export default processRevaluationHistory;
